import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/AdminSiteActivitiesPage.css";
import PaginationControls from "../components/PaginationControls";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { TabBar, EmptyState } from "../components/ListToolbar";

const emptyFilters = {
  from: "",
  to: "",
  actor_user_id: "",
  action_type: "",
  entity_type: "",
  outcome: "",
  q: "",
};

const actionLabelMap = {
  "auth.login": "Giriş",
  "auth.logout": "Çıkış",
  "leave.create": "İzin oluşturuldu",
  "leave.update": "İzin güncellendi",
  "leave.approve": "İzin onaylandı",
  "leave.reject": "İzin reddedildi",
  "leave.cancel": "İzin iptal edildi",
  "leave.document_download": "İzin PDF indirildi",
  "leave.report_upload": "Rapor PDF yüklendi",
  "leave.report_download": "Rapor PDF indirildi",
  "leave.edit_request": "İzin düzenleme talebi",
  "leave.edit_approve": "İzin düzenleme onayı",
  "leave.edit_reject": "İzin düzenleme reddi",
  "user.create": "Kullanıcı oluşturuldu",
  "user.update": "Kullanıcı güncellendi",
  "user.delete": "Kullanıcı silindi",
  "user.password_change": "Şifre değiştirildi",
  "user.bulk_import": "Toplu kullanıcı içe aktarıldı",
  "user.bulk_create": "Toplu kullanıcı oluşturuldu",
  "user.export_download": "Kullanıcı dışa aktarımı indirildi",
  "user.template_download": "Kullanıcı şablonu indirildi",
  "team.create": "Ekip oluşturuldu",
  "team.update": "Ekip güncellendi",
  "team.delete": "Ekip silindi",
  "leave_type.create": "İzin türü oluşturuldu",
  "leave_type.update": "İzin türü güncellendi",
  "leave_type.delete": "İzin türü silindi",
  "public_holiday.create": "Resmi tatil eklendi",
  "public_holiday.delete": "Resmi tatil silindi",
  "site_settings.update": "Site ayarı güncellendi",
};

const outcomeLabelMap = {
  success: "Başarılı",
  failed: "Başarısız",
  denied: "Yetki reddi",
  error: "Hata",
};

const prettifyKey = (value) =>
  String(value || "")
    .split(".")
    .map((part) => part.replace(/_/g, " "))
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" / ");

const toDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatTimestamp = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const AdminSiteActivitiesPage = () => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [facets, setFacets] = useState({ action_types: [], entity_types: [], outcomes: [] });
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const requestSeq = useRef(0); // geç dönen eski yanıt yeni sonucu ezmesin
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth > 760
  );

  const pageSizeKey = "pageSize.adminSiteActivities";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 25;
    return [10, 25, 50, 100].includes(parsed) ? parsed : 25;
  });

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const fetchUsers = async () => {
    try {
      const usersRes = await apiClient.get("/users");
      setUsers(usersRes.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    }
  };

  const fetchActivities = async (targetPage = page, targetFilters = filters, targetPageSize = pageSize) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const params = {
        page: targetPage,
        limit: targetPageSize,
      };

      Object.entries(targetFilters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params[key] = value;
        }
      });

      const res = await apiClient.get("/admin/site-activities", { params });
      if (seq !== requestSeq.current) return;
      const data = res.data || {};
      setInitialLoaded(true);
      setItems(data.items || []);
      setTotalItems(Number(data.pagination?.total || 0));
      setFacets(data.facets || { action_types: [], entity_types: [], outcomes: [] });

      if (!targetFilters?.from && !targetFilters?.to && data.range) {
        setFilters((prev) => ({
          ...prev,
          from: data.range.active_from || data.range.default_from || prev.from,
          to: data.range.active_to || data.range.default_to || prev.to,
        }));
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([fetchUsers(), fetchActivities(1, emptyFilters, pageSize)]);
      } catch (_) {
        // fetchActivities/fetchUsers handle their own errors
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filters.from && !filters.to) return;
    fetchActivities(page, filters, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = async (e) => {
    e.preventDefault();
    setPage(1);
    await fetchActivities(1, filters, pageSize);
  };

  const resetFilters = async () => {
    setPage(1);
    setFilters(emptyFilters);
    await fetchActivities(1, emptyFilters, pageSize);
  };

  // Sonuç (outcome) sekmesi: anında uygulanır
  const changeOutcomeTab = async (outcome) => {
    const next = { ...filters, outcome };
    setFilters(next);
    setPage(1);
    await fetchActivities(1, next, pageSize);
  };

  // Hızlı tarih aralıkları
  const applyPreset = async (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    const next = { ...filters, from: toDateInput(from), to: toDateInput(to) };
    setFilters(next);
    setPage(1);
    await fetchActivities(1, next, pageSize);
  };

  const actorOptions = useMemo(
    () =>
      (users || []).map((user) => ({
        value: String(user.id),
        label: `${user.name || ""} ${user.surname || ""} (${user.username || "-"})`.trim(),
      })),
    [users]
  );

  const labelForAction = (value) =>
    lang === "tr" ? actionLabelMap[value] || prettifyKey(value) : prettifyKey(value);
  const labelForOutcome = (value) =>
    lang === "tr" ? outcomeLabelMap[value] || prettifyKey(value) : prettifyKey(value);

  if (!initialLoaded && loading) return <div className="admin-site-activities">{t("common.loading")}</div>;
  if (!initialLoaded && error)
    return (
      <div className="admin-site-activities error">
        {t("common.error")}: {error}
        <div>
          <button className="ghost" type="button" onClick={() => fetchActivities(1, emptyFilters, pageSize)}>
            {t("adminActivities.retry")}
          </button>
        </div>
      </div>
    );

  return (
    <div className="admin-site-activities">
      <div className="admin-site-activities-header">
        <div>
          <h2>{t("admin.siteActivities.title")}</h2>
          <p className="muted">{t("admin.siteActivities.subtitle")}</p>
        </div>
      </div>

      <TabBar
        value={filters.outcome}
        onChange={changeOutcomeTab}
        ariaLabel={t("admin.siteActivities.filters.outcome")}
        tabs={[
          { key: "", label: t("adminActivities.tabs.all"), count: filters.outcome === "" ? totalItems : null },
          ...(facets.outcomes || []).map((o) => ({
            key: o,
            label: labelForOutcome(o),
            count: filters.outcome === o ? totalItems : null,
          })),
        ]}
      />

      <button
        type="button"
        className="ghost activity-filters-toggle"
        aria-expanded={filtersOpen}
        onClick={() => setFiltersOpen((v) => !v)}
      >
        {filtersOpen ? t("adminActivities.hideFilters") : t("adminActivities.showFilters")}
      </button>

      <form
        className={`activity-filters-card${filtersOpen ? "" : " is-collapsed"}`}
        onSubmit={applyFilters}
      >
        <div className="activity-presets">
          <button type="button" className="ghost" onClick={() => applyPreset(1)}>
            {t("adminActivities.presets.today")}
          </button>
          <button type="button" className="ghost" onClick={() => applyPreset(7)}>
            {t("adminActivities.presets.last7")}
          </button>
          <button type="button" className="ghost" onClick={() => applyPreset(30)}>
            {t("adminActivities.presets.last30")}
          </button>
        </div>
        <div className="activity-filters-grid">
          <label>
            {t("admin.siteActivities.filters.from")}
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange("from", e.target.value)}
            />
          </label>
          <label>
            {t("admin.siteActivities.filters.to")}
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange("to", e.target.value)}
            />
          </label>
          <label>
            {t("admin.siteActivities.filters.user")}
            <select
              value={filters.actor_user_id}
              onChange={(e) => handleFilterChange("actor_user_id", e.target.value)}
            >
              <option value="">{t("admin.siteActivities.filters.allUsers")}</option>
              {actorOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("admin.siteActivities.filters.action")}
            <select
              value={filters.action_type}
              onChange={(e) => handleFilterChange("action_type", e.target.value)}
            >
              <option value="">{t("admin.siteActivities.filters.allActions")}</option>
              {(facets.action_types || []).map((actionType) => (
                <option key={actionType} value={actionType}>
                  {labelForAction(actionType)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("admin.siteActivities.filters.entity")}
            <select
              value={filters.entity_type}
              onChange={(e) => handleFilterChange("entity_type", e.target.value)}
            >
              <option value="">{t("admin.siteActivities.filters.allEntities")}</option>
              {(facets.entity_types || []).map((entityType) => (
                <option key={entityType} value={entityType}>
                  {prettifyKey(entityType)}
                </option>
              ))}
            </select>
          </label>
          <label className="activity-filters-search">
            {t("admin.siteActivities.filters.search")}
            <input
              type="text"
              value={filters.q}
              onChange={(e) => handleFilterChange("q", e.target.value)}
              placeholder={t("admin.siteActivities.filters.searchPlaceholder")}
            />
          </label>
        </div>

        <div className="activity-filters-actions">
          <button className="primary" type="submit">
            {t("admin.siteActivities.filters.apply")}
          </button>
          <button className="ghost" type="button" onClick={resetFilters}>
            {t("admin.siteActivities.filters.reset")}
          </button>
          {error && <span className="error-text" role="alert">{error}</span>}
        </div>
      </form>

      <div className="table-wrap">
        <PaginationControls
          totalItems={totalItems}
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        <table className="activities-table">
          <thead>
            <tr>
              <th>{t("admin.siteActivities.columns.date")}</th>
              <th>{t("admin.siteActivities.columns.user")}</th>
              <th>{t("admin.siteActivities.columns.action")}</th>
              <th>{t("admin.siteActivities.columns.entity")}</th>
              <th>{t("admin.siteActivities.columns.detail")}</th>
              <th>{t("admin.siteActivities.columns.outcome")}</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td className="mono">{formatTimestamp(item.created_at)}</td>
                  <td>
                    <div>
                      {item.actor_name
                        ? `${item.actor_name} ${item.actor_surname || ""}`.trim()
                        : item.actor_name_snapshot || "-"}
                    </div>
                    <div className="muted small">
                      {item.actor_username || "-"} {item.actor_role_name ? `• ${item.actor_role_name}` : ""}
                    </div>
                  </td>
                  <td>
                    <div>{labelForAction(item.action_type)}</div>
                    <div className="muted small mono">{item.http_method || item.method} {item.request_path || item.path}</div>
                  </td>
                  <td>
                    <div>{prettifyKey(item.entity_type)}</div>
                    <div className="muted small mono">
                      {item.entity_id ? `#${item.entity_id}` : "-"}
                    </div>
                  </td>
                  <td>
                    <div>{item.entity_label || "-"}</div>
                    <div className="muted small mono">
                      {item.metadata?.body?.status || item.metadata?.file?.originalname || item.ip_address || "-"}
                    </div>
                  </td>
                  <td>
                    <span className={`activity-outcome outcome-${item.outcome}`}>
                      {labelForOutcome(item.outcome)}
                    </span>
                    <div className="muted small mono">{item.status_code}</div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        {!items.length && !loading && <EmptyState message={t("admin.siteActivities.empty")} />}
        <PaginationControls
          totalItems={totalItems}
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
};

export default AdminSiteActivitiesPage;
